import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import type { GetServerSidePropsContext } from 'next'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import { useDispatch } from 'react-redux'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import moment from 'moment'
import { FaArrowRight } from 'react-icons/fa6'

import Meta from 'lib/meta'
import { setSpaceRedirectUrl } from 'lib/setSpaceRedirectUrl'
import { addLocalePrefix } from 'lib/addLocalePrefix'

import { Prefecture } from 'state/ducks/prefecture/models'
import { Breadcrumb } from 'types/breadcrumb'
import { fetchCitiesRequest } from 'state/ducks/country/actions'
import countries from 'data/countries.json'

import { useAffiliateTracking } from 'hooks/tracking/useAffiliateTracking'
import { useConversionTracking } from 'features/tracking/hooks/useConversionTracking'
import { useStations } from 'features/facility/hooks/useStations'
import { useFacilities } from 'features/facility/hooks/useFacilities'
import { usePrefectures } from 'features/prefecture/hooks/usePrefectures'
import { getPrefectureCountryId } from 'features/prefecture/constants'
import { useValidateReservation } from 'features/space/hooks/useValidateReservation'
import { fetchSpaceDetail } from 'features/space/services/spaceDetailApi'
import type { Space, SpaceType } from 'features/space/types/spaceDetail'

import Layout from 'components/commons/layouts/Layout'
import ReviewSectionComponent from 'components/sections/ReviewSectionComponent'
import LocationSection from 'components/domains/top/LocationSection'
import BreadcrumbLayout from 'components/commons/layouts/BreadcrumbLayout'
import { BookingInvalidPopup } from 'components/modals/BookingInvalid'
import { SpaceNotFound } from 'components/Pages/Pudo/SpaceNotFound'

import { SpaceDetailSection } from 'features/space/components/SpaceDetailSection'
import {
  SPACE_BOOKING_SESSION_STORAGE_KEY,
  SpaceBookingPanel,
  SpaceBookingParams,
} from 'features/space/components/SpaceBookingPanel'
import { SpaceSpBookingOverlay } from 'features/space/components/SpaceSpBookingOverlay'

import styles from './styles.module.css'

type Props = {
  locale: string
  space: Space | null
  redirect?: string
}

const SpacesDetailPage: FC<Props> = ({ locale, space, redirect }) => {
  useAffiliateTracking()
  const { t } = useTranslation()
  const router = useRouter()
  const dispatch = useDispatch()
  const { saveRwgToken } = useConversionTracking(space?.space_id)
  const { validate } = useValidateReservation()

  const { stations, fetchStations } = useStations()
  const { groupedFacilities, fetchFacilities } = useFacilities()

  const [currentPrefecture, setCurrentPrefecture] = useState<Prefecture>(null)
  const [breadcrumbMenus, setBreadcrumbMenus] = useState<Breadcrumb[]>([])
  const [isOpenSpBooking, setOpenSpBooking] = useState(false)
  const [bookingInvalidMessage, setBookingInvalidMessage] = useState('')
  const [isLoading, setLoading] = useState(false)

  const fromGgMap = router.query.from_gg_maps === 'true'

  const createShareUrl = useCallback(() => {
    let url: string = `/${locale === 'default' ? 'ja' : locale}${router.asPath}`

    if (url.includes('?')) {
      url = url.split('?')[0]
    }

    if (url.includes('#')) {
      url = url.split('#')[0]
    }

    return url
  }, [router, locale])

  const spaceCountryCode = useMemo(() => {
    if (!space) return ''

    const country = countries.find((country) => country.id === space.country_id)

    return country?.country_code.toLowerCase()
  }, [space])

  const { prefectures } = usePrefectures({
    countryId: getPrefectureCountryId(spaceCountryCode),
    enabled: spaceCountryCode === 'jpn' || spaceCountryCode === 'twn',
  })

  const prefectureFromStore = useMemo(() => {
    if (!space?.prefecture_id) return undefined

    return prefectures
      ?.flatMap((area) => area.prefectures)
      ?.find((prefecture) => prefecture.id === space.prefecture_id)
  }, [prefectures, space?.prefecture_id])

  const getPrefectureName = useCallback(() => {
    if (!space) return ''

    return prefectureFromStore?.name ?? space.prefecture
  }, [prefectureFromStore, space])

  const getGetBreadcrumbMenus = useCallback(() => {
    if (!spaceCountryCode || !space) return []

    const menus = []

    let basePath: string = `/${locale}/${spaceCountryCode}/city`
    basePath = basePath.replace('zh_tw', 'zh-TW')

    if (space.prefecture_slug) {
      menus.push({
        label: space.prefecture,
        path: `${basePath}/${space.prefecture_slug}`,
      })
    }

    if (space.name) {
      menus.push({
        label: space.name,
      })
    }

    return menus
  }, [spaceCountryCode, locale, space])

  const navigateToBookingFlow = useCallback(
    (params: SpaceBookingParams) => {
      const { startDate, endDate, reservationItems, price, currency } = params
      const spaceType: SpaceType = space.space_type

      switch (spaceType) {
        case 'pudo': {
          const query = new URLSearchParams({
            space_id: space.space_id,
            booked_from: startDate,
            booked_to: endDate,
            reservation_items: JSON.stringify(reservationItems),
          })

          sessionStorage.setItem(
            SPACE_BOOKING_SESSION_STORAGE_KEY,
            JSON.stringify({
              reservationItems,
              startDate,
              startTime: moment(startDate).format('HH:mm'),
              endDate,
              endTime: moment(endDate).format('HH:mm'),
            })
          )

          router.push('/pudoBooking?' + query.toString())
          return
        }
        case 'locker':
        case 'coin_locker':
        case 'multiecube': {
          // /lockerBooking は legacy の encrypted_id (locker_id) 前提のため UUID ではなく encrypted_id を渡す
          const query = new URLSearchParams({
            locker_id: space.encrypted_id,
            booked_from: startDate,
            booked_to: endDate,
            small_box_count: String(reservationItems.small || 0),
            medium_box_count: String(reservationItems.medium || 0),
            large_box_count: String(reservationItems.large || 0),
          })

          router.push('/lockerBooking?' + query.toString())
          return
        }
        case 'normal':
        case 'jr_east':
        case 'japan_post':
        case 'jr_west':
        case 'nta':
        case 'apaman': {
          const start = moment(startDate, 'YYYY-MM-DD HH:mm')
          const end = moment(endDate, 'YYYY-MM-DD HH:mm')

          router.push({
            pathname: '/booking',
            query: {
              name: space.name,
              image: space.space_photos?.[0]?.url ?? space.main_image_url ?? '',
              open_at: space.working_hours?.open_at ?? '',
              close_at: space.working_hours?.close_at ?? '',
              check_in: start.format('YYYY年MM月DD日') + start.format('HH:mm'),
              check_out: end.format('YYYY年MM月DD日') + end.format('HH:mm'),
              currency: (currency || 'JPY').toLowerCase(),
              bag_size: reservationItems.small || 0,
              suitcase_size: reservationItems.large || 0,
              total_amount: price != null ? price.toLocaleString() : '',
              accept_on_site_payment: space.features.accept_on_site_payment,
              space_id: space.encrypted_id,
              booked_from: start.format(),
              booked_to: end.format(),
            },
          })
          return
        }
        default: {
          const exhaustiveCheck: never = spaceType
          return exhaustiveCheck
        }
      }
    },
    [space, router]
  )

  const handleBooking = useCallback(
    async (params: SpaceBookingParams) => {
      setLoading(true)

      const result = await validate({
        space_id: space.space_id,
        from: params.startDate,
        to: params.endDate,
        reservation_items: params.reservationItems,
      })

      if (!result.valid) {
        const message = result.errorMessages
          ? result.errorMessages.join('\n')
          : t('BOOKING.ERRORS.unexpected_error')

        setBookingInvalidMessage(message)
        setLoading(false)
        return
      }

      navigateToBookingFlow(params)

      setLoading(false)
    },
    [validate, space, navigateToBookingFlow, t]
  )

  useEffect(() => {
    if (!space) return

    // canonical URL への正規化 (旧 /spaces ページの挙動を踏襲)
    const pattern = /\/spaces\/[a-zA-Z0-9-]+($|\?|#)/
    if (!pattern.test(router.asPath)) {
      const queryString = router.asPath.includes('?')
        ? router.asPath.substring(router.asPath.indexOf('?'))
        : ''
      router.replace(`/spaces/${space.space_id}${queryString}`)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- canonical URL normalization on initial mount only
  }, [])

  useEffect(() => {
    setSpaceRedirectUrl()
  }, [])

  useEffect(() => {
    saveRwgToken()
  }, [saveRwgToken])

  useEffect(() => {
    if (!spaceCountryCode || !space) return

    if (spaceCountryCode === 'jpn') {
      if (space.prefecture_id) {
        fetchStations(space.prefecture_id)
        fetchFacilities(space.prefecture_id, { type: 'facility' })
      }
    } else if (spaceCountryCode === 'twn') {
      dispatch(fetchCitiesRequest(spaceCountryCode))
    }
  }, [dispatch, spaceCountryCode, space, fetchStations, fetchFacilities])

  useEffect(() => {
    if (!space?.prefecture_id) return

    setCurrentPrefecture({
      id: space.prefecture_id,
      slug: space.prefecture_slug,
      name: prefectureFromStore?.name ?? space.prefecture,
      lat: Number(space.latitude),
      lon: Number(space.longitude),
      en_name: prefectureFromStore?.name ?? space.prefecture,
      en_area_name: '',
      zh_cn_name: prefectureFromStore?.name ?? space.prefecture,
      zh_cn_area_name: '',
      zh_tw_name: prefectureFromStore?.name ?? space.prefecture,
      zh_tw_area_name: '',
      zoom_pc: 0,
      zoom_sp: 0,
    })
  }, [space, prefectureFromStore])

  useEffect(() => {
    const menus = getGetBreadcrumbMenus()
    setBreadcrumbMenus(menus)
  }, [getGetBreadcrumbMenus])

  useEffect(() => {
    if (redirect) router.replace(redirect)
  }, [redirect, router])

  if (redirect) {
    return null
  }

  if (!space) {
    return (
      <Layout>
        <SpaceNotFound />
      </Layout>
    )
  }

  // 構造化データ (develop /space と同様、レビューがある場合に LocalBusiness を出力)
  let jsonLd = null
  if (space.total_review_count > 0) {
    const countryCodeMap: Record<string, string> = { jpn: 'JP', twn: 'TW' }
    const addressCountry = countryCodeMap[spaceCountryCode] ?? spaceCountryCode
    const streetAddress = [space.detailed_place, space.building].filter(Boolean).join(' ')

    jsonLd = {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      name: space.name,
      image: space.space_photos?.[0]?.url || undefined,
      url: `${process.env.DOMAIN}${createShareUrl()}`.replace('/zh_tw', '/zh-TW'),
      address: {
        '@type': 'PostalAddress',
        streetAddress,
        addressLocality: space.city ?? '',
        addressRegion: space.prefecture ?? '',
        addressCountry,
      },
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: space.average_review_rating ? String(space.average_review_rating) : '0',
        bestRating: '5',
        ratingCount: space.total_review_count ? String(space.total_review_count) : '0',
      },
    }
  }

  return (
    <>
      <Layout>
        <Meta
          title={t('META.TITLE.SPACE_DETAIL_PAGE', { spaceName: space.name })}
          description={t('META.DESCRIPTION.SPACE_DETAIL_PAGE', { spaceName: space.name })}
          url={createShareUrl()}
          image={space.space_photos?.[0]?.url ?? ''}
          jsonLd={jsonLd && JSON.stringify(jsonLd)}
        />

        <div className={styles.container}>
          <div className={styles.spaceDetailContainer}>
            <SpaceDetailSection space={space} fromGgMap={fromGgMap} />
          </div>
          <div className={styles.spaceBookingContainer}>
            <div className={styles.stickyContainer}>
              <SpaceBookingPanel isLoading={isLoading} onBooking={handleBooking} space={space} />
            </div>
            <div onClick={() => setOpenSpBooking(true)} className={styles.bookingSPContainer}>
              <p>{t('LOCKER.BUTTON.CREATE_RESERVATION')}</p>
              <FaArrowRight size={20} color="#fff" />
            </div>
          </div>
        </div>
        {!!space.top_reviews.length && (
          <ReviewSectionComponent
            averageReviewRating={space?.average_review_rating}
            reviewsCount={space?.total_review_count}
            reviews={space?.top_reviews as any[]}
          />
        )}
        <div className={styles.sectionContainer}>
          <LocationSection
            locations={[]}
            prefectures={prefectures ?? []}
            isSelectedArea={false}
            onSelectArea={undefined}
            setIsSelectedArea={() => {}}
            stations={[]}
            facilities={[]}
            locale={locale}
            countryCode={spaceCountryCode}
            isShowBackButton={false}
            isShowAreaName={false}
            sectionTitle={t('AREAS.CITY.SLUG.LOCATION_SECTION_TITLE_AREA')}
          />
        </div>
        {spaceCountryCode === 'jpn' && !!currentPrefecture && (
          <div className={styles.sectionContainer}>
            <LocationSection
              locations={[]}
              isSelectedArea={true}
              onSelectArea={() => {}}
              setIsSelectedArea={() => {}}
              selectedPrefecture={currentPrefecture}
              stations={stations}
              facilities={groupedFacilities?.facilities ?? []}
              locale={locale}
              countryCode={spaceCountryCode}
              isShowBackButton={false}
              isShowAreaName={false}
              sectionTitle={t('AREAS.CITY.SLUG.LOCATION_SECTION_TITLE_PLACE_PREFECTURE', {
                place: getPrefectureName(),
              })}
              areaPlaceListHeadingStyle="h3"
              placeName={getPrefectureName()}
            />
          </div>
        )}
        <BreadcrumbLayout menus={breadcrumbMenus} />
      </Layout>
      <SpaceSpBookingOverlay
        space={space}
        isLoading={isLoading}
        onBooking={handleBooking}
        visible={isOpenSpBooking}
        onClose={() => setOpenSpBooking(false)}
      />
      <BookingInvalidPopup
        message={bookingInvalidMessage}
        onClose={() => setBookingInvalidMessage('')}
      />
    </>
  )
}

export default SpacesDetailPage

export async function getServerSideProps(context: GetServerSidePropsContext) {
  const { locale, query, resolvedUrl } = context

  const idParam = query.id
  const segments = Array.isArray(idParam) ? idParam : idParam ? [String(idParam)] : []
  const spaceUuid = segments.length ? segments[segments.length - 1] : undefined
  const isWalkin = segments.length >= 2 && segments.at(-1) === 'walkin'

  if (isWalkin) {
    const queryIndex = resolvedUrl.indexOf('?')
    const queryString = queryIndex === -1 ? '' : resolvedUrl.slice(queryIndex)

    // Redirect on the client so GA can record the `/walkin` page view before
    // navigating to the canonical space detail URL.
    return {
      props: {
        redirect: addLocalePrefix(`/spaces/${segments[0]}${queryString}`, locale),
        ...(await serverSideTranslations(locale, ['common'])),
      },
    }
  }

  if (!spaceUuid) {
    return { notFound: true }
  }

  const result = await fetchSpaceDetail(spaceUuid, locale)

  if (result.isSpaceNotFound) {
    return { notFound: true }
  }

  if (!result.isOk) {
    throw new Error('Failed to fetch space detail')
  }

  return {
    props: {
      space: result.data,
      locale: locale.replace('-', '_').toLowerCase(),
      ...(await serverSideTranslations(locale, ['common'])),
    },
  }
}
